import { useMemo } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faMap, faChartLine, faEnvelope, faUser, faGaugeHigh, faFileLines } from '@fortawesome/free-solid-svg-icons'
import { LAYOUT_DIMENSIONS } from '../hooks/useResponsiveLayout'
import { useReportsAccess } from '../hooks/useReports'

interface RailItem {
  path: string
  icon: typeof faMap
  label: string
  matchPaths: string[]
}

const railItems: RailItem[] = [
  {
    path: '/dashboard',
    icon: faGaugeHigh,
    label: 'Dashboard',
    matchPaths: ['/dashboard'],
  },
  {
    path: '/map',
    icon: faMap,
    label: 'Map',
    matchPaths: ['/', '/map'],
  },
  {
    path: '/charts',
    icon: faChartLine,
    label: 'Charts',
    matchPaths: ['/charts', '/compare'],
  },
  {
    path: '/reports',
    icon: faFileLines,
    label: 'Reports',
    matchPaths: ['/reports'],
  },
  {
    path: '/messages',
    icon: faEnvelope,
    label: 'Msg',
    matchPaths: ['/messages'],
  },
  {
    path: '/profile',
    icon: faUser,
    label: 'Account',
    matchPaths: ['/profile', '/preferences', '/settings'],
  },
]

export function SideRailNav() {
  // Reports appears only for administrators and for users whose group has
  // a report configured; everyone else never sees the tab.
  const { data: hasReportsAccess } = useReportsAccess()
  const visibleItems = railItems.filter(
    (item) => item.path !== '/reports' || hasReportsAccess
  )
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Preserve sigfoxId query parameter across navigation
  const sigfoxId = searchParams.get('sigfoxId')
  const queryString = useMemo(() => {
    return sigfoxId ? `?sigfoxId=${sigfoxId}` : ''
  }, [sigfoxId])

  // Check if an item is active based on current path
  const isActive = (item: RailItem): boolean => {
    return item.matchPaths.some((path) => {
      if (path === '/') {
        return location.pathname === '/'
      }
      return location.pathname.startsWith(path)
    })
  }

  return (
    <nav
      className="fixed top-0 right-0 h-full bg-white border-l border-gray-200 shadow-lg z-40 flex flex-col"
      style={{ width: LAYOUT_DIMENSIONS.SIDE_RAIL_WIDTH }}
      role="navigation"
      aria-label="Main navigation"
    >
      {/* Rail items */}
      <div className="flex-1 flex flex-col items-center py-4 space-y-2">
        {visibleItems.map((item) => {
          const active = isActive(item)
          // Don't add query string to account pages
          const href =
            item.path === '/profile' ? item.path : `${item.path}${queryString}`

          return (
            <Link
              key={item.path}
              to={href}
              className={`flex flex-col items-center justify-center w-full min-h-[56px] py-2 px-1 transition-colors touch-manipulation ${
                active
                  ? 'text-blue-600 bg-blue-50'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-50 active:bg-gray-100'
              }`}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
            >
              <FontAwesomeIcon
                icon={item.icon}
                className={`text-lg mb-1 ${active ? 'text-blue-600' : ''}`}
                fixedWidth
              />
              <span
                className={`text-[10px] font-medium leading-tight text-center ${
                  active ? 'text-blue-600' : ''
                }`}
              >
                {item.label}
              </span>
            </Link>
          )
        })}
      </div>
    </nav>
  )
}
